/**
 * DashboardEditorPage — Dashboard editor with responsive widget grid,
 * drag-and-drop rearrangement, widget management, and settings panel.
 *
 * Requirements: 2.1, 2.4, 2.6, 2.7, 2.8, 2.9, 3.1, 3.5, 4.10
 */

import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { validateTitle } from '@coreflow/shared';
import type { AnalyticsDashboard, AnalyticsWidget, WidgetLayoutEntry, QueryPayload } from '@coreflow/shared';
import * as api from '../services/api-client';
import * as analyticsApi from '../services/analytics-api';
import { QueryBuilderDialog } from '../components/analytics/QueryBuilderDialog';
import { WidgetCard } from '../components/analytics/WidgetCard';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_WIDGETS = 99;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DashboardWithOwner extends AnalyticsDashboard {
  readonly ownerId?: string;
}

interface DashboardState {
  dashboard: DashboardWithOwner | null;
  widgets: AnalyticsWidget[];
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;
}

type DashboardAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; dashboard: DashboardWithOwner; widgets: AnalyticsWidget[] }
  | { type: 'LOAD_ERROR'; error: string }
  | { type: 'UPDATE_DASHBOARD'; dashboard: DashboardWithOwner }
  | { type: 'UPDATE_LAYOUT'; layout: WidgetLayoutEntry[] }
  | { type: 'REMOVE_WIDGET'; widgetId: string }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'SET_ERROR'; error: string | null };

function dashboardReducer(state: DashboardState, action: DashboardAction): DashboardState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, isLoading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, isLoading: false, dashboard: action.dashboard, widgets: action.widgets };
    case 'LOAD_ERROR':
      return { ...state, isLoading: false, error: action.error };
    case 'UPDATE_DASHBOARD':
      return { ...state, dashboard: action.dashboard };
    case 'UPDATE_LAYOUT':
      return state.dashboard
        ? { ...state, dashboard: { ...state.dashboard, widgetLayout: action.layout } }
        : state;
    case 'REMOVE_WIDGET':
      return {
        ...state,
        widgets: state.widgets.filter((w) => w.widgetId !== action.widgetId),
      };
    case 'SET_SAVING':
      return { ...state, isSaving: action.isSaving };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Gets the current user ID from the stored tokens (simple extraction) */
function getCurrentUserId(): string | null {
  try {
    const stored = localStorage.getItem('auth_tokens');
    if (!stored) return null;
    const tokens = JSON.parse(stored);
    if (!tokens.idToken) return null;
    const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));
    return payload.sub ?? null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function DashboardEditorPage(): React.ReactElement {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(dashboardReducer, {
    dashboard: null,
    widgets: [],
    isLoading: true,
    error: null,
    isSaving: false,
  });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [queryBuilderOpen, setQueryBuilderOpen] = useState(false);
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [widgetToDelete, setWidgetToDelete] = useState<string | null>(null);

  const currentUserId = useRef(getCurrentUserId());

  // Determine if current user is the dashboard owner
  const isOwner = state.dashboard?.ownerId
    ? state.dashboard.ownerId === currentUserId.current
    : true; // Default to owner when ownerId isn't available (own dashboard)

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchDashboard = useCallback(async () => {
    if (!dashboardId) return;
    dispatch({ type: 'LOAD_START' });
    try {
      const response = await api.get<{ data: DashboardWithOwner & { widgets?: AnalyticsWidget[]; readOnly?: boolean } }>(
        `/v1/analytics/dashboards/${dashboardId}`,
      );
      const dashboardData = response.data;
      const widgets = dashboardData.widgets ?? [];
      // Remove widgets from dashboard object to keep state clean
      const { widgets: _w, readOnly, ...dashboard } = dashboardData;
      dispatch({ type: 'LOAD_SUCCESS', dashboard: { ...dashboard, ownerId: readOnly ? 'other' : undefined }, widgets });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to load dashboard';
      dispatch({ type: 'LOAD_ERROR', error: message });
    }
  }, [dashboardId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // -------------------------------------------------------------------------
  // Drag-and-drop handlers
  // -------------------------------------------------------------------------

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLElement>, widgetId: string) => {
      if (!isOwner) return;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', widgetId);
      setDraggedWidgetId(widgetId);
    },
    [isOwner],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLElement>, targetWidgetId: string) => {
      e.preventDefault();
      const sourceWidgetId = e.dataTransfer.getData('text/plain');
      setDraggedWidgetId(null);

      if (!sourceWidgetId || sourceWidgetId === targetWidgetId || !state.dashboard) return;

      // Swap positions in the layout
      const layout: WidgetLayoutEntry[] = [...state.dashboard.widgetLayout];
      const sourceIdx = layout.findIndex((l) => l.widgetId === sourceWidgetId);
      const targetIdx = layout.findIndex((l) => l.widgetId === targetWidgetId);

      if (sourceIdx === -1 || targetIdx === -1) return;

      const sourceEntry = layout[sourceIdx];
      const targetEntry = layout[targetIdx];
      if (!sourceEntry || !targetEntry) return;

      // Swap the layout positions
      layout[sourceIdx] = { widgetId: sourceEntry.widgetId, width: sourceEntry.width, height: sourceEntry.height, col: targetEntry.col, row: targetEntry.row };
      layout[targetIdx] = { widgetId: targetEntry.widgetId, width: targetEntry.width, height: targetEntry.height, col: sourceEntry.col, row: sourceEntry.row };

      dispatch({ type: 'UPDATE_LAYOUT', layout });

      // Persist layout via API
      try {
        await api.put(`/v1/analytics/dashboards/${state.dashboard.dashboardId}`, {
          widgetLayout: layout,
          version: state.dashboard.version,
        });
      } catch {
        dispatch({ type: 'SET_ERROR', error: 'Failed to save layout' });
      }
    },
    [state.dashboard],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedWidgetId(null);
  }, []);

  // -------------------------------------------------------------------------
  // Widget deletion
  // -------------------------------------------------------------------------

  const handleDeleteWidget = useCallback(
    async (widgetId: string) => {
      if (!dashboardId) return;
      dispatch({ type: 'SET_SAVING', isSaving: true });
      try {
        await api.del(`/v1/analytics/dashboards/${dashboardId}/widgets/${widgetId}`);
        dispatch({ type: 'REMOVE_WIDGET', widgetId });
        // Also remove from layout
        if (state.dashboard) {
          const newLayout = state.dashboard.widgetLayout.filter(
            (l) => l.widgetId !== widgetId,
          );
          dispatch({ type: 'UPDATE_LAYOUT', layout: newLayout });
        }
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : 'Failed to delete widget';
        dispatch({ type: 'SET_ERROR', error: message });
      } finally {
        dispatch({ type: 'SET_SAVING', isSaving: false });
        setWidgetToDelete(null);
      }
    },
    [dashboardId, state.dashboard],
  );

  // -------------------------------------------------------------------------
  // Dashboard deletion
  // -------------------------------------------------------------------------

  const handleDeleteDashboard = useCallback(async () => {
    if (!dashboardId) return;
    dispatch({ type: 'SET_SAVING', isSaving: true });
    try {
      await api.del(`/v1/analytics/dashboards/${dashboardId}`);
      navigate('/analytics');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Deletion failed, please try again';
      dispatch({ type: 'SET_ERROR', error: message });
      dispatch({ type: 'SET_SAVING', isSaving: false });
    }
  }, [dashboardId, navigate]);

  // -------------------------------------------------------------------------
  // Add widget handler
  // -------------------------------------------------------------------------

  const handleAddWidget = useCallback(() => {
    setQueryBuilderOpen(true);
  }, []);

  const widgetLimitReached = state.widgets.length >= MAX_WIDGETS;

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------

  if (state.isLoading) {
    return (
      <main className="min-h-screen bg-background p-6" role="main" aria-busy="true">
        <div className="mx-auto max-w-7xl">
          <div
            className="flex items-center justify-center py-12"
            role="status"
            aria-label="Loading dashboard"
          >
            <div
              className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"
              aria-hidden="true"
            />
            <span className="ml-3 text-muted-foreground">Loading dashboard…</span>
          </div>
        </div>
      </main>
    );
  }

  // -------------------------------------------------------------------------
  // Error state
  // -------------------------------------------------------------------------

  if (state.error && !state.dashboard) {
    return (
      <main className="min-h-screen bg-background p-6" role="main">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-md border border-destructive bg-destructive/10 p-4" role="alert">
            <p className="text-sm text-destructive">{state.error}</p>
          </div>
          <Button className="mt-4" onClick={fetchDashboard}>
            Retry
          </Button>
        </div>
      </main>
    );
  }

  if (!state.dashboard) return <></>;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <main className="min-h-screen bg-background p-6" role="main">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/analytics')}
              className="rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Back to analytics"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-foreground truncate max-w-md">
              {state.dashboard.title}
            </h1>
            {!isOwner && (
              <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                View only
              </span>
            )}
          </div>

          {/* Owner actions */}
          {isOwner && (
            <div className="flex items-center gap-2">
              <Button
                onClick={handleAddWidget}
                disabled={widgetLimitReached}
                aria-label={
                  widgetLimitReached
                    ? 'Widget limit reached (maximum 99)'
                    : 'Add widget'
                }
                title={
                  widgetLimitReached
                    ? 'Maximum of 99 widgets per dashboard reached'
                    : undefined
                }
              >
                Add Widget
              </Button>
              <Button
                variant="outline"
                onClick={() => setSettingsOpen(true)}
                aria-label="Dashboard settings"
              >
                Settings
              </Button>
              <DeleteDashboardButton
                onConfirm={handleDeleteDashboard}
                disabled={state.isSaving}
              />
            </div>
          )}
        </header>

        {/* Inline error banner */}
        {state.error && state.dashboard && (
          <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3" role="alert">
            <p className="text-sm text-destructive">{state.error}</p>
          </div>
        )}

        {/* Widget limit message */}
        {isOwner && widgetLimitReached && (
          <div className="mb-4 rounded-md border border-yellow-300 bg-yellow-50 p-3" role="status">
            <p className="text-sm text-yellow-800">
              Maximum of 99 widgets per dashboard reached. Remove a widget to add more.
            </p>
          </div>
        )}

        {/* Empty state */}
        {state.widgets.length === 0 ? (
          <section
            className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted py-16"
            aria-label="No widgets"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="mb-4 text-muted-foreground"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
            <p className="mb-4 text-muted-foreground">
              This dashboard has no widgets yet.
            </p>
            {isOwner && (
              <Button onClick={handleAddWidget} disabled={widgetLimitReached}>
                Add Your First Widget
              </Button>
            )}
          </section>
        ) : (
          /* Widget grid */
          <section
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            aria-label="Dashboard widgets"
            role="list"
          >
            {state.widgets.map((widget) => (
              <WidgetGridItem
                key={widget.widgetId}
                widget={widget}
                dashboardId={dashboardId!}
                isOwner={isOwner}
                isDragging={draggedWidgetId === widget.widgetId}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                onDelete={() => setWidgetToDelete(widget.widgetId)}
              />
            ))}
          </section>
        )}

        {/* Widget delete confirmation */}
        <AlertDialog
          open={widgetToDelete !== null}
          onOpenChange={(open) => { if (!open) setWidgetToDelete(null); }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Widget</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this widget? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (widgetToDelete) handleDeleteWidget(widgetToDelete);
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Query Builder dialog */}
        {dashboardId && (
          <QueryBuilderDialog
            open={queryBuilderOpen}
            onOpenChange={setQueryBuilderOpen}
            dashboardId={dashboardId}
            onWidgetCreated={fetchDashboard}
          />
        )}

        {/* Settings panel */}
        {settingsOpen && state.dashboard && (
          <DashboardSettingsPanel
            dashboard={state.dashboard}
            onClose={() => setSettingsOpen(false)}
            onUpdate={(updated) => {
              dispatch({ type: 'UPDATE_DASHBOARD', dashboard: updated });
            }}
          />
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// WidgetGridItem — fetches live query data and renders WidgetCard
// ---------------------------------------------------------------------------

interface WidgetGridItemProps {
  readonly widget: AnalyticsWidget;
  readonly dashboardId: string;
  readonly isOwner: boolean;
  readonly isDragging: boolean;
  readonly onDragStart: (e: React.DragEvent<HTMLElement>, widgetId: string) => void;
  readonly onDragOver: (e: React.DragEvent<HTMLElement>) => void;
  readonly onDrop: (e: React.DragEvent<HTMLElement>, widgetId: string) => void;
  readonly onDragEnd: () => void;
  readonly onDelete: () => void;
}

function WidgetGridItem({
  widget,
  dashboardId,
  isOwner,
  isDragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDelete,
}: WidgetGridItemProps): React.ReactElement {
  const [data, setData] = useState<Array<{ dimensionValue: string; measureValue: number }> | null>(null);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchWidgetData() {
      try {
        const payload: QueryPayload = {
          boardIds: widget.dataSourceBoardIds,
          dimension: widget.dimension,
          measure: widget.measure,
          filters: widget.filters.length > 0 ? widget.filters : undefined,
        };
        const results = await analyticsApi.executeQuery(payload);
        if (!cancelled) {
          setData(results);
          setHasAccess(true);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        // Check if it's a 403 (no access to boards)
        if (err && typeof err === 'object' && 'statusCode' in err && (err as { statusCode: number }).statusCode === 403) {
          setHasAccess(false);
        }
        // On any error, set data to empty array to show error/empty state
        setData([]);
      }
    }

    fetchWidgetData();
    return () => { cancelled = true; };
  }, [widget.dataSourceBoardIds, widget.dimension, widget.measure, widget.filters]);

  return (
    <div
      role="listitem"
      draggable={isOwner}
      onDragStart={(e) => onDragStart(e, widget.widgetId)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, widget.widgetId)}
      onDragEnd={onDragEnd}
      className={`transition-all ${
        isDragging ? 'opacity-50 ring-2 ring-primary' : ''
      } ${isOwner ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <WidgetCard
        widget={widget}
        data={data}
        hasAccess={hasAccess}
        isOwner={isOwner}
        dashboardId={dashboardId}
        onDelete={onDelete}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeleteDashboardButton
// ---------------------------------------------------------------------------

interface DeleteDashboardButtonProps {
  readonly onConfirm: () => void;
  readonly disabled: boolean;
}

function DeleteDashboardButton({ onConfirm, disabled }: DeleteDashboardButtonProps): React.ReactElement {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm" disabled={disabled} aria-label="Delete dashboard">
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Dashboard</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this dashboard and all its widgets? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Delete Dashboard</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------------------------------------------------------------------------
// DashboardSettingsPanel
// ---------------------------------------------------------------------------

interface DashboardSettingsPanelProps {
  readonly dashboard: DashboardWithOwner;
  readonly onClose: () => void;
  readonly onUpdate: (dashboard: DashboardWithOwner) => void;
}

function DashboardSettingsPanel({
  dashboard,
  onClose,
  onUpdate,
}: DashboardSettingsPanelProps): React.ReactElement {
  const [title, setTitle] = useState(dashboard.title);
  const [visibility, setVisibility] = useState<'private' | 'public-read'>(dashboard.visibility);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTitle(value);
    const result = validateTitle(value);
    setTitleError(result.valid ? null : (result.error ?? null));
  }, []);

  const handleSave = useCallback(async () => {
    const validation = validateTitle(title);
    if (!validation.valid) {
      setTitleError(validation.error ?? 'Invalid title');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await api.put<DashboardWithOwner>(
        `/v1/analytics/dashboards/${dashboard.dashboardId}`,
        {
          title: title.trim(),
          visibility,
          version: dashboard.version,
        },
      );
      onUpdate(updated);
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? (err as { message: string }).message
          : 'Failed to save settings';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  }, [title, visibility, dashboard, onUpdate, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-end"
      role="dialog"
      aria-label="Dashboard settings"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside className="relative z-10 h-full w-full max-w-md overflow-y-auto bg-background border-l shadow-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Dashboard Settings</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close settings"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Title field */}
        <div className="mb-4">
          <label htmlFor="dashboard-title" className="mb-1 block text-sm font-medium text-foreground">
            Title
          </label>
          <input
            id="dashboard-title"
            type="text"
            value={title}
            onChange={handleTitleChange}
            maxLength={100}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-describedby={titleError ? 'title-error' : undefined}
            aria-invalid={titleError ? 'true' : undefined}
          />
          {titleError && (
            <p id="title-error" className="mt-1 text-xs text-destructive" role="alert">
              {titleError}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            {title.trim().length}/100 characters
          </p>
        </div>

        {/* Visibility toggle */}
        <div className="mb-6">
          <span className="mb-2 block text-sm font-medium text-foreground">Visibility</span>
          <div className="flex gap-2" role="radiogroup" aria-label="Dashboard visibility">
            <button
              type="button"
              role="radio"
              aria-checked={visibility === 'private'}
              onClick={() => setVisibility('private')}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                visibility === 'private'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input text-muted-foreground hover:bg-accent'
              }`}
            >
              Private
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={visibility === 'public-read'}
              onClick={() => setVisibility('public-read')}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                visibility === 'public-read'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input text-muted-foreground hover:bg-accent'
              }`}
            >
              Public (read-only)
            </button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {visibility === 'private'
              ? 'Only you can view this dashboard.'
              : 'Any authenticated user can view this dashboard.'}
          </p>
        </div>

        {/* Save error */}
        {saveError && (
          <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3" role="alert">
            <p className="text-sm text-destructive">{saveError}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={isSaving || !!titleError}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
        </div>
      </aside>
    </div>
  );
}
